import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { useNotesDocsAPI } from 'src/SharedCustomHooks/UseNotesAPI/useNotesDocsAPI';
import { INoteFormConfig } from 'src/SharedCustomHooks/UseNotesAPI/types';

interface NotesDataType {
  notes: any[];
  documents: any[];
  faqsNotesDocuments: any[];
  loadingNotesDocs: boolean;
  notesError: string | null;
  searchTerm: string;
}

interface NotesActionsType {
  fetchNotes: (itemId: string) => Promise<void>;
  saveNote: (note: any, itemId: string) => Promise<void>;
  updateNote: (note: any, itemId: string) => Promise<void>;
  deleteNote: (note: any, itemId: string) => Promise<void>;
  setSearchTerm: (searchTerm: string) => void;
  clearSearch: () => void;
  clearNotesState: () => void;
}

interface NotesProviderProps {
  children: ReactNode;
}

interface NotesContextConfig {
  itemType: string;
  displayName: string;
}

export function createNotesContextProvider(config: NotesContextConfig) {
  const { itemType, displayName } = config;

  const DataContext = createContext<NotesDataType | undefined>(undefined);
  const ActionsContext = createContext<NotesActionsType | undefined>(undefined);

  const NotesProvider = ({ children }: NotesProviderProps) => {
    const noteConfig: INoteFormConfig = {
      noteName: 'NOTES',
      noteType: 'NOTES',
      ItemType: itemType,
    };

    const notesAPI = useNotesDocsAPI();
    const [searchTerm, setSearchTerm] = useState<string>('');

    const saveNote = async (rawNote: any, itemId: string): Promise<void> => {
      try {
        await notesAPI.saveNotes(rawNote, itemId, noteConfig);
      } catch (error) {
        console.error(`Error saving ${displayName} note:`, error);
        throw error;
      }
    };

    const updateNote = async (rawNote: any, itemId: string): Promise<void> => {
      try {
        await notesAPI.updateNote(rawNote, itemId, noteConfig);
      } catch (error) {
        console.error(`Error updating ${displayName} note:`, error);
        throw error;
      }
    };

    const deleteNote = async (note: any, itemId: string): Promise<void> => {
      try {
        const noteWithType = {
          ...note,
          Type: 'NOTES',
          ItemType: itemType,
        };

        await notesAPI.deleteNote(noteWithType, itemId);
      } catch (error) {
        console.error(`Error deleting ${displayName} note:`, error);
        throw error;
      }
    };

    const setSearchTermHandler = (term: string): void => {
      setSearchTerm(term);
    };

    const clearSearchTerm = (): void => {
      setSearchTerm('');
    };

    const dataValue = useMemo(
      () => ({
        notes: notesAPI.notes,
        documents: notesAPI.documents,
        faqsNotesDocuments: notesAPI.faqsNotesDocuments,
        loadingNotesDocs: notesAPI.loadingNotesDocs,
        notesError: notesAPI.notesError,
        searchTerm,
      }),
      [
        notesAPI.notes,
        notesAPI.documents,
        notesAPI.faqsNotesDocuments,
        notesAPI.loadingNotesDocs,
        notesAPI.notesError,
        searchTerm,
      ]
    );

    const actionsValue = useMemo(
      () => ({
        fetchNotes: notesAPI.fetchNotesAndDocuments,
        saveNote,
        updateNote,
        deleteNote,
        setSearchTerm: setSearchTermHandler,
        clearSearch: clearSearchTerm,
        clearNotesState: notesAPI.clearNotesState,
      }),
      [
        notesAPI.fetchNotesAndDocuments,
        notesAPI.clearNotesState,
        saveNote,
        updateNote,
        deleteNote,
      ]
    );

    return (
      <ActionsContext.Provider value={actionsValue}>
        <DataContext.Provider value={dataValue}>
          {children}
        </DataContext.Provider>
      </ActionsContext.Provider>
    );
  };

  const useNotesDataFromContext = (): NotesDataType => {
    const context: NotesDataType = useContext(DataContext);

    if (context === undefined) {
      throw new Error(
        `useNotesDataFromContext must be used within a ${itemType}NotesProvider`
      );
    }

    return context;
  };

  const useNotesActionsFromContext = (): NotesActionsType => {
    const context: NotesActionsType = useContext(ActionsContext);

    if (context === undefined) {
      throw new Error(
        `useNotesActionsFromContext must be used within a ${itemType}NotesProvider`
      );
    }

    return context;
  };

  return {
    NotesProvider,
    useNotesDataFromContext,
    useNotesActionsFromContext,
  };
}
